import type { ParsedUrlQuery } from 'querystring'

/**
 * Attribution parameter keys used across analytics tracking
 */

/** Facebook Click ID parameter */
export const FBCLID_KEY = 'fbclid'

/** UTM parameter keys */
export const UTM_PARAM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_ad',
    'utm_id',
]

/** Google Ads specific parameter keys (URL query param names) */
export const GOOGLE_ADS_PARAM_KEYS = [
    'gclid',
    'adset',
    'network',
    'matchtype',
    'device',
]

/** Google iOS 14.5+ conversion tracking parameter keys */
export const GOOGLE_IOS_PARAM_KEYS = ['wbraid', 'gbraid']

/** TikTok parameter keys */
export const TIKTOK_PARAM_KEYS = ['ttclid']

/** Snapchat parameter keys */
export const SNAPCHAT_PARAM_KEYS = ['ScCid']

/** Impact Radius parameter keys */
export const IMPACT_RADIUS_PARAM_KEYS = ['clickid', 'im_ref']

/** Meta Pixel trait keys for Segment */
export const META_PIXEL_TRAIT_KEYS = ['fbc', 'fbp', 'fbclid']

/**
 * Additional attribution parameters not covered by platform-specific arrays.
 * Used for URL preservation in both proxy middleware and client-side link building.
 */
export const ADDITIONAL_ATTRIBUTION_PARAMS = [
    'pid', // AppsFlyer partner ID
    'c', // AppsFlyer campaign name parameter
    'fbc', // Meta Click cookie (fb.1.{timestamp}.{fbclid})
    'fbp', // Meta Browser cookie (fb.1.{timestamp}.{random})
]

/** All attribution parameter keys including UTM, Google Ads params, and click IDs */
export const ATTRIBUTION_PARAM_KEYS = [
    ...UTM_PARAM_KEYS,
    ...GOOGLE_ADS_PARAM_KEYS,
    ...GOOGLE_IOS_PARAM_KEYS,
    ...TIKTOK_PARAM_KEYS,
    ...SNAPCHAT_PARAM_KEYS,
    FBCLID_KEY,
]

/** PII keys that should only be stored in user profile, NOT in events */
export const PII_KEYS = [
    'firstName',
    'lastName',
    'dateOfBirth',
    'birthday', // Same as dateOfBirth but in ISO format (YYYY-MM-DD) for Segment
    'email',
    'phone',
]

/** User attribute keys that should only be tracked once (immutable or rarely change) */
export const TRACK_ONCE_KEYS = [FBCLID_KEY, ...META_PIXEL_TRAIT_KEYS]

/**
 * Default UTM values for organic/direct traffic.
 * These are set by ClickTracker when no UTM params are present in the URL.
 * Used to ensure Looker shows "attributed to direct" instead of "unattributed".
 */
export const UTM_DEFAULT_VALUES = ['direct', 'website'] as const

/**
 * Check if a UTM value is a default (not a real campaign value).
 * Used to prevent overwriting real UTM values with defaults when users
 * navigate from landing pages (with UTM params) to other pages (without).
 */
export function isDefaultUtmValue(value?: string): boolean {
    return (
        typeof value === 'string' &&
        UTM_DEFAULT_VALUES.includes(
            value as (typeof UTM_DEFAULT_VALUES)[number],
        )
    )
}

/**
 * Returns the new value if it should override the current value.
 * Prevents default values ('direct', 'website') from overwriting real attribution.
 */
export const resolveUtmValue = (
    newValue: string | undefined,
    currentValue: string | null,
): string | null =>
    newValue && (!currentValue || !isDefaultUtmValue(newValue))
        ? newValue
        : currentValue

/**
 * Extract URL query parameters as strings.
 * Converts array values to their first element.
 */
export function extractQueryParams(
    query: ParsedUrlQuery,
    keys: readonly string[],
): Record<string, string> {
    return keys.reduce(
        (acc, key) => {
            const value = query[key]
            if (value !== undefined) {
                acc[key] = Array.isArray(value) ? value[0] : value
            }
            return acc
        },
        {} as Record<string, string>,
    )
}

/**
 * Server-side cookie names for Safari ITP persistence.
 * These cookies are set via Next.js middleware with 90-day expiration,
 * bypassing Safari's 7-day limit on client-side JavaScript cookies.
 */
export const SERVER_COOKIE_KEYS = {
    gclid: '_gcl_aw',
    fbclid: '_fbc_server',
    impactClickId: '_irc',
    wbraid: '_wbraid',
    gbraid: '_gbraid',
    ttclid: '_ttclid',
    ScCid: '_sccid',
} as const

/**
 * Read a cookie value by name from document.cookie
 */
export function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
    // Return the raw cookie value without decoding.
    // Cookie values are already stored decoded by the proxy middleware (src/proxy.ts),
    // so decoding again would corrupt values containing URL-like sequences (e.g., %2F in gclid).
    return match ? match[2] : null
}

/**
 * Parse gclid from _gcl_aw cookie value.
 *
 * Google's gtag.js stores gclid in the format: GCL.{version}.{timestamp}.{gclid}
 * Example: GCL.1.1770239967.Cj0KCQiA-YvMBhDtARIsAHZuUzJuSCff...
 *
 * Our proxy middleware stores it as the raw gclid value.
 *
 * This function handles both formats and always returns the raw gclid.
 */
export function parseGclidFromCookie(
    cookieValue: string | null,
): string | null {
    if (!cookieValue) return null

    // If cookie starts with "GCL.", extract the raw gclid
    // Format: GCL.{version}.{timestamp}.{gclid}
    // Note: gclid itself may contain dots, so we rejoin everything after the 3rd dot
    if (cookieValue.startsWith('GCL.')) {
        const parts = cookieValue.split('.')
        if (parts.length >= 4) {
            return parts.slice(3).join('.')
        }
        // Malformed GCL cookie - return null to avoid sending invalid data
        return null
    }

    // Otherwise return as-is (already raw gclid from proxy)
    return cookieValue
}

/**
 * Get attribution data from server-set cookies.
 * Used as fallback when URL params are missing (e.g., Safari ITP expired client cookies).
 *
 * Priority: URL params > client cookies > server cookies
 */
export function getServerCookieAttribution(): Record<string, string | null> {
    return {
        // Parse gclid to handle gtag.js cookie format (GCL.{version}.{timestamp}.{gclid})
        gclid: parseGclidFromCookie(getCookie(SERVER_COOKIE_KEYS.gclid)),
        fbclid: getCookie(SERVER_COOKIE_KEYS.fbclid),
        fbc: getCookie(SERVER_COOKIE_KEYS.fbclid), // _fbc_server contains fbc format
        impactClickId: getCookie(SERVER_COOKIE_KEYS.impactClickId),
        wbraid: getCookie(SERVER_COOKIE_KEYS.wbraid),
        gbraid: getCookie(SERVER_COOKIE_KEYS.gbraid),
        ttclid: getCookie(SERVER_COOKIE_KEYS.ttclid),
        ScCid: getCookie(SERVER_COOKIE_KEYS.ScCid),
    }
}
